import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import {
  FontTextSmBold,
  FontTextSmNormal,
  PrimitivesBluegray50,
  PrimitivesBluegray700,
  PrimitivesBluegray900,
  PrimitivesOrange50,
  PrimitivesOrange500,
} from '@ambulnz/design-tokens';
import { getFieldLabel, objectToString } from '../eligibilityAgentUtils';

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path
      d="M13.4792 10.8333H3.33333V9.16667H13.4792L8.8125 4.5L10 3.33333L16.6667 10L10 16.6667L8.8125 15.5L13.4792 10.8333Z"
      fill={PrimitivesBluegray700}
    />
  </svg>
);

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  gap: '16px',
  padding: '16px 24px',
  backgroundColor: PrimitivesBluegray50,
  borderRadius: '8px',
});

const SectionTitle = styled(Typography)({
  ...FontTextSmBold,
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
  textAlign: 'left',
  alignSelf: 'stretch',
});

const ChangesTable = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  gap: '16px',
});

const TableHeader = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '140px 1fr 24px 1fr',
  gap: '16px',
  alignSelf: 'stretch',
});

const TableRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '140px 1fr 24px 1fr',
  gap: '16px',
  alignSelf: 'stretch',
  alignItems: 'start',
});

const ColumnHeader = styled(Typography)({
  ...FontTextSmBold,
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
  textAlign: 'left',
});

const RowLabel = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray700,
  textAlign: 'left',
});

const ValueText = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
  textAlign: 'left',
});

const ArrowCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MessageRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  backgroundColor: PrimitivesOrange50,
  borderRadius: '6px',
  borderLeft: `4px solid ${PrimitivesOrange500}`,
});

function ChangesSection({ title, changes, beforeLabel = 'Before', afterLabel = 'After' }) {
  if (!changes || changes.length === 0) return null;

  const renderChangeRow = (label, beforeValue, afterValue, key) => (
    <TableRow key={key}>
      <RowLabel>{label}</RowLabel>
      <ValueText>{beforeValue || '-'}</ValueText>
      <ArrowCell>
        <ArrowIcon />
      </ArrowCell>
      <ValueText>{afterValue || '-'}</ValueText>
    </TableRow>
  );

  const renderMessageOnlyRow = (message, key) => (
    <MessageRow key={key}>
      <ValueText>{message}</ValueText>
    </MessageRow>
  );

  const renderEvent = (event, index) => {
    if (!event.key || (!event.before && !event.after)) {
      return renderMessageOnlyRow(event.message || event.name, `${title}-${index}`);
    }

    return renderChangeRow(
      getFieldLabel(event.key),
      objectToString(event.before),
      objectToString(event.after),
      `${title}-${index}`,
    );
  };

  return (
    <Container>
      <SectionTitle>{title}</SectionTitle>
      <ChangesTable>
        <TableHeader>
          <Box />
          <ColumnHeader>{beforeLabel}</ColumnHeader>
          <Box />
          <ColumnHeader>{afterLabel}</ColumnHeader>
        </TableHeader>
        {changes.map((event, index) => renderEvent(event, index))}
      </ChangesTable>
    </Container>
  );
}

ChangesSection.propTypes = {
  title: PropTypes.string.isRequired,
  changes: PropTypes.arrayOf(PropTypes.shape({})),
  beforeLabel: PropTypes.string,
  afterLabel: PropTypes.string,
};

export default ChangesSection;
